import { Link } from "react-router-dom";
import { Mail, Clock, Stethoscope, ShieldCheck, ChevronRight } from "lucide-react";
import PageLayout from "../components/pages/PageLayout";
import ContactForm from "../components/site/ContactForm";
import { Reveal } from "../components/site/Reveal";
import { SITE } from "../seo/site";
import { SERVICE_LINKS } from "../content/siteLinks";

const POINTS = [
  { icon: Clock, title: "Reply within one business day", text: "Tell us about your product idea and we will come back with next steps and a time for a free discovery call." },
  { icon: Stethoscope, title: "Speak with the formulation team", text: "Your call is with the doctors and formulators who will actually develop your product." },
  { icon: ShieldCheck, title: "Confidential by default", text: "Every conversation and brief is treated under NDA; your idea stays yours." },
];

const ContactIntro = () => (
  <div>
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-navy/55">
      <Link to="/" className="hover:text-navy transition-colors duration-300">Home</Link>
      <ChevronRight size={12} strokeWidth={1.5} />
      <span className="text-navy">Contact</span>
    </nav>
    <p className="mt-10 text-xs uppercase tracking-[0.3em] font-semibold text-navy/60 mb-6">Let&apos;s Talk</p>
    <h1 data-testid="page-h1" className="font-display text-navy text-4xl sm:text-5xl lg:text-6xl leading-[1.06] tracking-tight">
      Let&apos;s build <em className="italic font-medium">your product.</em>
    </h1>
    <p className="mt-8 text-base md:text-lg leading-relaxed text-charcoal/85 max-w-xl">
      Tell us a little about your brand and the product you want to create — skincare, haircare, Ayurveda, wellness or nutraceuticals. We&apos;ll set up a free discovery call and map the fastest route to launch.
    </p>
    <ul className="mt-10 space-y-6">
      {POINTS.map(({ icon: Icon, title, text }) => (
        <li key={title} className="flex gap-4">
          <span className="mt-0.5 grid place-items-center w-10 h-10 rounded-full border border-navy/20 text-navy shrink-0">
            <Icon size={18} strokeWidth={1.5} />
          </span>
          <div>
            <p className="font-display text-lg text-navy">{title}</p>
            <p className="mt-1 text-sm leading-relaxed text-charcoal/75 max-w-md">{text}</p>
          </div>
        </li>
      ))}
    </ul>
    <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="mt-10 inline-flex items-center gap-3 text-sm font-semibold text-navy border-b border-navy/30 pb-1 hover:border-navy transition-colors duration-300">
      <Mail size={16} strokeWidth={1.5} /> {SITE.email}
    </a>
  </div>
);

const ServiceIndex = () => (
  <section data-testid="contact-service-index" className="py-20 bg-powder/50 border-t border-navy/10">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <p className="text-xs uppercase tracking-[0.3em] font-semibold text-navy/60 mb-6">Not sure where to start?</p>
      <div className="flex flex-wrap gap-3">
        {SERVICE_LINKS.map(({ label, path }) => (
          <Link key={path} to={path} data-testid={`contact-service-link-${path.slice(1)}`} className="rounded-full border border-navy/20 bg-cream px-5 py-2.5 text-sm text-navy transition-colors duration-300 hover:bg-navy hover:text-ivory">
            {label}
          </Link>
        ))}
      </div>
    </div>
  </section>
);

export default function Contact() {
  return (
    <PageLayout testId="contact-page" showCta={false}>
      <section data-testid="contact-hero" className="pt-[76px] bg-ivory">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-[1fr_0.95fr] gap-14 lg:gap-20 py-16 lg:py-24">
          <Reveal y={20}>
            <ContactIntro />
          </Reveal>
          <Reveal y={20} delay={0.15}>
            <div className="rounded-2xl bg-cream border border-navy/10 p-7 sm:p-10 shadow-[0_24px_50px_rgba(62,74,46,0.12)]">
              <h2 className="font-display text-2xl text-navy">Request a free discovery call</h2>
              <p className="mt-2 text-sm text-charcoal/70">Fields marked * are required.</p>
              <ContactForm idPrefix="contact-page" />
            </div>
          </Reveal>
        </div>
      </section>
      <ServiceIndex />
    </PageLayout>
  );
}
